import { useEffect } from 'react';
import {
  EyeIcon,
  MapPinIcon,
  CloudIcon,
  ServerIcon,
  BuildingLibraryIcon,
  AcademicCapIcon,
  GlobeEuropeAfricaIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ArrowPathIcon,
  MapIcon,
} from '@heroicons/react/24/outline';
import { useDashboardContext } from '../components/Layout';

/**
 * Sources.jsx — data provenance reference page
 * ================================================
 *
 * BF1-3: lifts the data-source attribution out of the bottom DataSourcesBar
 * (which is hidden on mobile and tight on desktop) into a proper page that
 * fits any viewport. Linked from the sidebar / mobile drawer.
 */

const SOURCES = [
  {
    Icon:      EyeIcon,
    iconColor: 'text-green-400',
    name:      'Copernicus Sentinel-2',
    family:    'EU Copernicus Programme',
    what:      'Multispectral optical imagery — vegetation health (NDVI from B4/B8), surface water classification, drought stress indicators.',
    cadence:   'Tile revisit ≈ 5 days (per region)',
    href:      'https://www.esa.int/Applications/Observing_the_Earth/Copernicus/Sentinel-2',
  },
  {
    Icon:      MapPinIcon,
    iconColor: 'text-indigo-400',
    name:      'Galileo GNSS',
    family:    'EU GNSS Programme',
    what:      'Precision positioning & timing for the EO data pipeline — anchors imagery to ground truth and timestamps acquisitions.',
    cadence:   'Continuous',
    href:      'https://www.gsc-europa.eu/galileo/what-is-galileo',
  },
  {
    Icon:      CloudIcon,
    iconColor: 'text-sky-400',
    name:      'OpenMeteo',
    family:    'Weather data API',
    what:      'Hourly precipitation, temperature, soil moisture & forecast. Source for short-term flood / drought signals + the historical ERA5 archive used by replay.',
    cadence:   'Hourly updates · Historical archive 1940 → present',
    href:      'https://open-meteo.com/',
  },
  {
    Icon:      ServerIcon,
    iconColor: 'text-orange-400',
    name:      'ECMWF ERA5',
    family:    'Reanalysis',
    what:      'Global atmospheric reanalysis used for SPI / PDSI drought indices and replay-mode reconstruction of past events.',
    cadence:   'Daily archive (5-day lag)',
    href:      'https://www.ecmwf.int/en/forecasts/dataset/ecmwf-reanalysis-v5',
  },
  {
    Icon:      BuildingLibraryIcon,
    iconColor: 'text-teal-400',
    name:      'NIMH Bulgaria',
    family:    'Bulgarian National Institute of Meteorology & Hydrology',
    what:      'Authoritative national meteorology — threshold calibration, regional baselines, curated reference events for replay.',
    cadence:   'Daily bulletins · WMO 1991-2020 normals',
    href:      'https://www.meteo.bg/en/',
  },
  {
    Icon:      AcademicCapIcon,
    iconColor: 'text-emerald-400',
    name:      'WMO — World Meteorological Organization',
    family:    'United Nations Specialised Agency',
    what:      'Standardized Precipitation Index (SPI) user guide and drought indicator handbooks — basis for drought threshold calibration and classification rules.',
    cadence:   'Reference standards',
    href:      'https://who.int/',
  },
  {
    Icon:      GlobeEuropeAfricaIcon,
    iconColor: 'text-violet-400',
    name:      'World Bank CCKP — Bulgaria',
    family:    'Climate Change Knowledge Portal',
    what:      'Bulgaria climate context for the 1991–2020 climatology derived from observed historical data. Used to understand current climate conditions and future climate scenarios for regional baseline calibration.',
    cadence:   'Annual updates',
    href:      'https://climateknowledgeportal.worldbank.org/country/bulgaria/climate-data-historical',
  },
  {
    Icon:      DocumentTextIcon,
    iconColor: 'text-purple-400',
    name:      'IPCC',
    family:    'Intergovernmental Panel on Climate Change',
    what:      'Climate risk scenarios and extreme event projections — long-term context for flood and drought risk assessment in Bulgaria.',
    cadence:   'Assessment cycles every 5–7 years',
    href:      'https://www.ipcc.ch/',
  },
  {
    Icon:      ChartBarIcon,
    iconColor: 'text-amber-400',
    name:      'Eurostat',
    family:    'Statistical Office of the European Union',
    what:      'EU-level environmental and climate statistics — regional context for Bulgaria\'s flood and drought exposure across oblasts.',
    cadence:   'Annual updates',
    href:      'https://ec.europa.eu/eurostat/web/main/home',
  },
  {
    Icon:      MapIcon,
    iconColor: 'text-lime-400',
    name:      'QGIS',
    family:    'Open-Source GIS Platform',
    what:      'Free and open-source desktop GIS used for AOI polygon creation, geospatial data preparation, and visualization of EO outputs — underpins the HydroTwin region boundaries and watershed analysis.',
    cadence:   'Open-source tool',
    href:      'https://qgis.org/',
  },
  {
    Icon:      ArrowPathIcon,
    iconColor: 'text-rose-400',
    name:      'ISME-HYDRO',
    family:    'Water Resources Management Platform · Mozaika',
    what:      'Comprehensive dam and river monitoring system combining in-situ measurements, satellite data, and deep learning. Official platform of the Bulgarian Executive Agency for Exploitation and Monitoring of the Danube River — issues flood and drought advance warnings for the Danube basin.',
    cadence:   'Historical archive · in-situ + satellite records',
    href:      'https://isme-hydro.com/',
  },
];

export default function Sources() {
  const { setPageMeta } = useDashboardContext();

  useEffect(() => {
    setPageMeta({
      title:        'Data sources',
      subtitle:     'Provenance & refresh cadence across the HydroTwin pipeline',
      showSeverity: false,
    });
  }, [setPageMeta]);

  return (
    <div className="absolute inset-0 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-col gap-5">
        <section className="bg-gray-900/60 border border-gray-800 rounded-xl p-5">
          <h2 className="text-base font-bold text-white mb-1">Powered by trusted data</h2>
          <p className="text-xs text-gray-400 leading-relaxed">
            Every assessment HydroTwin emits is grounded in open Earth-observation
            and weather data. Below is the full list of upstream feeds, what we
            extract from each, and how often they update.
          </p>
        </section>

        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SOURCES.map(s => (
            <li
              key={s.name}
              className="bg-gray-900/60 border border-gray-800 rounded-xl p-5 flex flex-col gap-2"
            >
              <div className="flex items-start gap-3">
                <s.Icon className={`w-6 h-6 flex-shrink-0 ${s.iconColor}`} aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white leading-tight">{s.name}</p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">
                    {s.family}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-300 leading-relaxed">{s.what}</p>
              <div className="flex items-center justify-between gap-3 mt-1 pt-2 border-t border-gray-800/60">
                <span className="text-[10px] text-gray-500">
                  <span className="uppercase tracking-widest">Cadence:</span>{' '}
                  <span className="text-gray-300">{s.cadence}</span>
                </span>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[10px] uppercase tracking-widest text-cyan-400 hover:text-cyan-300
                             underline whitespace-nowrap"
                >
                  Visit ↗
                </a>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
